import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useGenerativeTask from '@/components/LyearnAI/hooks/useGenerativeTask';
import { GenerativeTaskStatusEnum, GenerativeTaskTypeEnum } from '@/types/schema';

import { VideoSlideFromSelection } from './useVideoSlideSelection';

export interface GenerateVideoPayload {
  avatarId: string;
  voiceId: string;
  blockId: string;
  slides: Array<VideoSlideFromSelection>;
}

interface UseVideoGenerationProps {
  onRenderStart?: (gtId: string) => void;
}

function parseGeneratedVideoUrl(rawResult: string | null | undefined) {
  if (!rawResult) return null;
  try {
    const parsed = JSON.parse(rawResult);
    const firstCompleted = parsed?.videos?.find(
      (v: { status: string }) => v.status === 'completed',
    );
    return firstCompleted?.videoUrl ?? null;
  } catch {
    return null;
  }
}

export function useVideoGeneration(props: UseVideoGenerationProps) {
  const { onRenderStart } = props;
  const { t } = useTranslation('Course');
  const { showErrorSnackbar } = useSnackbar();
  const handleTaskError = useEvent((message?: string | null) => {
    showErrorSnackbar({
      message: message || t('Course:AiVideoGeneration.FailedToGenerateVideo'),
    });
  });
  const { executeCreateGenerativeTask, generativeTask, generativeTaskId, fetching } =
    useGenerativeTask({
      onCreateSuccess: onRenderStart,
      onTaskError: (error) => {
        handleTaskError(error?.message);
      },
      suppressDefaultErrorHandling: true,
    });

  const generateVideo = useEvent((payload: GenerateVideoPayload) => {
    executeCreateGenerativeTask({
      type: GenerativeTaskTypeEnum.VideoGeneration,
      input: {
        videoGeneration: {
          avatarId: payload.avatarId,
          voiceId: payload.voiceId,
          blockId: payload.blockId,
          slides: payload.slides.map((slide) => ({
            slideTemplateId: slide.slideTemplateId,
            heygenTemplateId: slide.heygenTemplateId, // redundant, BE should be able to resolve heygenTemplateId from slideTemplateId
            name: slide.name, // FIXME: redundant, BE should be able to resolve heygenTemplateId and name from slideTemplateId
            transcript: slide.transcript,
            variables: slide.variables ? JSON.stringify(slide.variables) : undefined,
          })),
        },
      },
    });
  });

  const isCreatingVideo = fetching || (!!generativeTaskId && !generativeTask);

  const videoGeneration = generativeTask?.result?.videoGeneration;
  const legacyVideoUrl =
    videoGeneration?.videoUrl || parseGeneratedVideoUrl(videoGeneration?.videoUrl);

  const generatedVideoUrl = videoGeneration?.videoPlaylist?.videoUrl || legacyVideoUrl;
  const generatedCaptionUrl = videoGeneration?.videoPlaylist?.captionUrl;

  const totalVideosCount = videoGeneration?.videos?.length ?? 0;
  const videoGenerationProgress = generativeTask?.progress ?? null;
  const completedVideosCount =
    videoGeneration?.videos?.filter((v) => v.status === 'completed').length ?? 0;

  const isRenderingVideo =
    !generatedVideoUrl &&
    !!generativeTask &&
    [GenerativeTaskStatusEnum.Processing, GenerativeTaskStatusEnum.ToBeProcessed].includes(
      generativeTask.status,
    );

  return {
    generativeTaskId,
    generateVideo,
    isCreatingVideo,
    isRenderingVideo,
    generatedVideoUrl,
    generatedCaptionUrl,
    completedVideosCount,
    totalVideosCount,
    videoGenerationProgress,
  };
}
